import React, { useState } from 'react';
import { Button, Icon } from '@mui/material';
import ApiError from 'src/api/ApiError';

const VoiceRecorder: React.FC<{
  onRecordingComplete: (audioFile: File) => void;
  isTranscribing?: boolean;
  loading?: boolean;
  error?: ApiError | null;
  id?: string;
  size?: number;
}> = ({
  onRecordingComplete,
  isTranscribing = false,
  loading = false,
  id = 'voice-recorder-button',
  size = 40,
}) => {
  const [recording, setRecording] = useState(false);
  const [mediaRecorder, setMediaRecorder] = useState<MediaRecorder | null>(
    null,
  );

  const startRecording = async () => {
    if (isTranscribing || loading) return; // Don't start if busy

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream);
      const chunks: BlobPart[] = [];

      recorder.ondataavailable = (e) => chunks.push(e.data);

      recorder.onstop = () => {
        const audioBlob = new Blob(chunks, { type: 'audio/wav' });
        const audioFile = new File([audioBlob], 'recording.wav', {
          type: 'audio/wav',
        });
        onRecordingComplete(audioFile);
        stream.getTracks().forEach((track) => track.stop()); // Clean up
      };

      recorder.start();
      setMediaRecorder(recorder);
      setRecording(true);
    } catch (error) {
      console.error('Error accessing microphone:', error);
    }
  };

  const stopRecording = () => {
    if (mediaRecorder && mediaRecorder.state === 'recording') {
      mediaRecorder.stop();
      setRecording(false);
      setMediaRecorder(null);
    }
  };

  const isDisabled = isTranscribing || loading;
  const isShowingSpinner = isTranscribing || loading;

  const handlePointerDown = (e: React.PointerEvent) => {
    e.preventDefault();
    if (!isDisabled && !recording) {
      startRecording();
    }
  };

  const handlePointerUp = (e: React.PointerEvent) => {
    e.preventDefault();
    if (recording) {
      stopRecording();
    }
  };

  const handlePointerLeave = (e: React.PointerEvent) => {
    e.preventDefault();
    if (recording) {
      stopRecording();
    }
  };

  const getTitle = () => {
    if (loading) return 'Please wait for current operation to complete';
    if (isTranscribing) return 'Transcribing audio...';
    if (recording) return 'Release to stop recording';
    return 'Hold to record voice message';
  };

  return (
    <Button
      id={id}
      variant="outlined"
      onPointerDown={handlePointerDown}
      onPointerUp={handlePointerUp}
      onPointerLeave={handlePointerLeave}
      disabled={isDisabled}
      title={getTitle()}
      sx={{
        minWidth: `${size}px`,
        width: `${size}px`,
        height: `${size}px`,
        backgroundColor: recording ? '#d32f2f' : 'transparent',
        borderColor: recording ? '#d32f2f' : '#1976d2',
        color: recording ? 'white' : '#1976d2',
        '&:hover': {
          backgroundColor: recording ? '#c62828' : 'rgba(25, 118, 210, 0.04)',
          borderColor: recording ? '#c62828' : '#1976d2',
        },
        '&:disabled': {
          backgroundColor: '#f5f5f5',
          borderColor: '#e0e0e0',
          color: '#bdbdbd',
        },
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 0,
        userSelect: 'none',
        touchAction: 'none',
        '& .MuiIcon-root': {
          color: recording
            ? 'white !important'
            : isDisabled
              ? '#bdbdbd !important'
              : '#1976d2 !important',
        },
      }}
    >
      <Icon
        sx={{
          fontSize: `${Math.max(18, size * 0.45)}px`,
          ...(isShowingSpinner
            ? {
                animation: 'spin 1s linear infinite',
                '@keyframes spin': {
                  '0%': { transform: 'rotate(0deg)' },
                  '100%': { transform: 'rotate(360deg)' },
                },
              }
            : {}),
        }}
      >
        {isShowingSpinner ? 'sync' : recording ? 'mic' : 'mic_none'}
      </Icon>
    </Button>
  );
};

export default VoiceRecorder;
